import { createDownloadTask, getDownloadTaskStatus, downloadTaskFile } from '@/api/downloadTask'

let pollTimer = null

/** 后端 PDF 导出并发上限为 1，多个 PDF 任务需串行：上一项完成后再创建下一项 */
const PDF_QUEUE_TYPES = new Set(['document-pdf', 'document-rasterized-pdf', 'document-latex-pdf'])
let pdfExportSerialChain = Promise.resolve()

/** 浏览器触发 <a download> 串行：并发下载时部分环境会导致前一个 Promise 不结束、状态卡在「正在下载」 */
let browserDownloadChain = Promise.resolve()

const state = {
  tasks: []
}

const getters = {
  activeTasks: (state) => state.tasks.filter(t => t.status === 'pending' || t.status === 'running'),
  allTasks: (state) => [...state.tasks].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
}

const mutations = {
  ADD_OR_UPDATE_TASK(state, task) {
    const id = String(task.id)
    const idx = state.tasks.findIndex(t => String(t.id) === id)
    const existing = idx >= 0 ? state.tasks[idx] : null
    const t = {
      ...task,
      id,
      updatedAt: task.updatedAt ? new Date(task.updatedAt).getTime() : Date.now(),
      // 保留前端下载状态：idle | downloading | downloaded
      downloadState: task.downloadState ?? existing?.downloadState ?? 'idle'
    }
    if (idx >= 0) {
      state.tasks.splice(idx, 1, t)
    } else {
      state.tasks.unshift(t)
    }
  },
  SET_TASK_DOWNLOAD_STATE(state, { taskId, downloadState }) {
    const tid = String(taskId)
    const t = state.tasks.find(x => String(x.id) === tid)
    if (t) t.downloadState = downloadState
  },
  REMOVE_TASK(state, taskId) {
    const tid = String(taskId)
    state.tasks = state.tasks.filter(t => String(t.id) !== tid)
  },
  CLEAR_COMPLETED(state) {
    state.tasks = state.tasks.filter(t => t.status !== 'success' && t.status !== 'error')
  }
}

const actions = {
  /**
   * 非 PDF 任务立即创建；PDF 类任务进入全局串行队列，避免后端「并发上限 1」报错
   */
  async createTask(ctx, payload) {
    const type = payload?.type
    if (!PDF_QUEUE_TYPES.has(type)) {
      return ctx.dispatch('createTaskCore', payload)
    }
    const next = pdfExportSerialChain.then(async () => {
      const id = await ctx.dispatch('createTaskCore', { ...payload, __skipPolling: true })
      await ctx.dispatch('waitForPdfTaskTerminal', id)
      return id
    })
    pdfExportSerialChain = next.catch(() => {})
    return next
  },

  async createTaskCore({ commit, dispatch }, { type, params, label, __skipPolling = false }) {
    const resolvedLabel = label || getDefaultLabel(type, params)
    const res = await createDownloadTask(type, params, resolvedLabel)
    const task = res?.data?.task
    if (!task) throw new Error('创建任务失败')
    const full = { ...task, label: resolvedLabel }
    commit('ADD_OR_UPDATE_TASK', full)
    const tid = String(task.id)
    if (!__skipPolling) {
      dispatch('startPolling', tid)
    }
    return tid
  },

  /** 等到 PDF 任务成功或失败；成功后在队列内完成浏览器下载，再放行下一个 PDF 任务 */
  async waitForPdfTaskTerminal({ dispatch, state }, taskId) {
    const maxMs = 40 * 60 * 1000
    const start = Date.now()
    const tid = String(taskId)

    const pick = () => state.tasks.find(t => String(t.id) === tid)

    while (Date.now() - start < maxMs) {
      let task = pick()
      if (!task) {
        await dispatch('fetchTaskStatus', taskId)
        task = pick()
      }
      if (task?.status === 'success') {
        if (!task.fileName) {
          await dispatch('fetchTaskStatus', tid)
          task = pick() || task
        }
        if (task?.fileName && task.downloadState !== 'downloaded') {
          await dispatch('enqueueBrowserDownload', { taskId: tid, fileName: task.fileName })
        }
        return
      }
      if (task?.status === 'error') {
        const msg = task.error || task.message
        throw new Error(typeof msg === 'string' && msg.trim() ? msg : '导出失败')
      }
      await new Promise(r => setTimeout(r, 1000))
      await dispatch('fetchTaskStatus', taskId)
    }
    throw new Error('等待 PDF 导出超时，请稍后在任务中心查看')
  },

  async fetchTaskStatus({ commit }, taskId) {
    const tid = String(taskId)
    const res = await getDownloadTaskStatus(tid)
    const task = res?.data?.task
    if (task) {
      commit('ADD_OR_UPDATE_TASK', { ...task, id: tid })
    }
    return task
  },
  async downloadFile(_, { taskId, fileName }) {
    await downloadTaskFile(String(taskId), fileName)
  },

  /**
   * 将浏览器下载排入队列，避免多任务同时 <a.click> 导致状态异常
   */
  async enqueueBrowserDownload({ commit, dispatch }, { taskId, fileName }) {
    const tid = String(taskId)
    const run = async () => {
      commit('SET_TASK_DOWNLOAD_STATE', { taskId: tid, downloadState: 'downloading' })
      try {
        await dispatch('downloadFile', { taskId: tid, fileName })
        commit('SET_TASK_DOWNLOAD_STATE', { taskId: tid, downloadState: 'downloaded' })
      } catch (e) {
        console.error('[downloadTasks] 浏览器下载失败', tid, e)
        commit('SET_TASK_DOWNLOAD_STATE', { taskId: tid, downloadState: 'download_failed' })
        throw e
      }
    }
    const done = browserDownloadChain.then(run)
    browserDownloadChain = done.catch(() => {})
    return done
  },

  async retryDownload({ state, dispatch }, taskId) {
    const tid = String(taskId)
    const task = state.tasks.find(t => String(t.id) === tid)
    if (!task || task.status !== 'success' || !task.fileName) return
    await dispatch('enqueueBrowserDownload', { taskId: tid, fileName: task.fileName })
  },

  /**
   * 一次轮询：刷新所有进行中的任务。此前仅用单个 interval + 单个 taskId，
   * 新建任务时会 clearInterval，导致前一个班的复习题等任务不再被查询、进度条假死。
   */
  async pollTick({ rootState, dispatch }) {
    const tasks = rootState.downloadTasks?.tasks || []
    const activeIds = tasks
      .filter(t => t.status === 'pending' || t.status === 'running')
      .map(t => String(t.id))
    if (activeIds.length === 0) {
      dispatch('stopPolling')
      return
    }
    for (const tid of activeIds) {
      try {
        const updated = await dispatch('fetchTaskStatus', tid)
        if (updated?.status === 'success') {
          void dispatch('enqueueBrowserDownload', { taskId: tid, fileName: updated.fileName })
        }
      } catch (e) {
        console.error('轮询任务状态失败:', e)
      }
    }
  },

  /** 确保全局仅一个定时器；多任务并发时共同复用，不再互相顶替 */
  startPolling({ dispatch }) {
    if (pollTimer) return
    const run = () => {
      void dispatch('pollTick')
    }
    run()
    pollTimer = setInterval(run, 2000)
  },
  stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  },
  removeTask({ commit, dispatch, rootState }, taskId) {
    commit('REMOVE_TASK', taskId)
    const remaining = rootState.downloadTasks?.tasks || []
    const hasActive = remaining.some(t => t.status === 'pending' || t.status === 'running')
    if (!hasActive) dispatch('stopPolling')
  },
  clearCompleted({ commit }) {
    commit('CLEAR_COMPLETED')
  }
}

function getDefaultLabel(type, params) {
  if (type === 'bct-review-zip') return `复习题 - ${params?.docTitle || '未命名'}`
  if (type === 'bct-review-batch-zip') return `批量复习题 - ${params?.docTitle || '未命名'}`
  if (type === 'class-wrong-zip') return `班级错题 - ${params?.classId || '未命名班级'}`
  if (type === 'document-pdf') return `PDF文档 - ${params?.title || '未命名'}`
  if (type === 'document-rasterized-pdf') return `PDF文档(栅格化) - ${params?.title || '未命名'}`
  if (type === 'document-latex-pdf') return `PDF - ${params?.title || '未命名'}`
  if (type === 'document-slides') return `课件 - ${params?.docName || '未命名'}`
  return `下载 - ${type}`
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
